<template>
  <scrollable-container :class="$style.scrollableContainer">
    <placeholder
      v-show="isTableAwaiting"
      :text="text"
      @buttonClick="buildReport()"
    />
    <loader v-show="isTableLoading" />
    <container
      v-show="isTableReady"
      id="printable"
      :class="[{ [$style.message]: isTableAwaiting }, $style.scrollableContent]"
    >
      <div :class="$style.header">
        <div
          v-if="total"
          :class="$style.total"
        >
          <div>Входящий трафик - {{ getDataField(total.sumTrafficIn, 'sumTrafficIn') }}</div>
          <div>Исходящий трафик - {{ getDataField(total.sumTrafficOut, 'sumTrafficOut') }}</div>
          <div>Время на сайтах - {{ getDataField(total.duration, 'duration') }}</div>
        </div>

        <search v-if="!isTrafficByGroups" />
      </div>

      <div v-if="isTrafficByGroups">
        <internet-table
          :columns="columns"
          :report-tree="reportTree"
          report-data="trafficByGroups"
        />
        <tabset
          :tabs="tabsChart"
          :opened="true"
          :hide-arrow="true"
          :tab-style="tabStyle"
          @tabClick="openNewTab"
        />
        <div
          v-if="tabsChart[0].active"
          :class="$style.bar"
        >
          <bar-chart
            :title="'Трафик по ПК'"
            :labels="labels"
            :datasets="datasets"
            :format-value="formatTraffic"
          />
        </div>
        <div
          v-if="tabsChart[1].active"
          :class="$style.bar"
        >
          <pie-chart
            :data="pieChartData"
            :format-value="convertSecondsToDays"
          />
        </div>
      </div>

      <div v-else>
        <tabset
          :tabs="tabs"
          :opened="true"
          :hide-arrow="true"
          :tab-style="tabStyle"
          @tabClick="openNewTab"
        />
        <stat-table
          v-if="tabs[2].active"
          :class="$style.table"
          :columns="columnsStat"
          :table-data="tableData"
        />
        <v-table
          v-if="!tabs[2].active"
          :data="tableData"
          :columns="columnsDetailed"
        >
          <template v-slot:column-sumTrafficIn="item">
            {{ item.row.sumTrafficIn | formatTraffic }}
          </template>
          <template v-slot:column-sumTrafficOut="item">
            {{ item.row.sumTrafficOut | formatTraffic }}
          </template>
        </v-table>

        <div
          v-if="!tabs[2].active"
          :class="$style.apexchart"
        >
          <div :class="$style.chart">
            <pie-chart
              :data="pieChartUserData.sumTrafficIn"
              :format-value="formatTraffic"
            />
          </div>
          <div :class="$style.chart">
            <pie-chart
              :data="pieChartUserData.sumTrafficOut"
              :format-value="formatTraffic"
            />
          </div>
        </div>
      </div>
    </container>
  </scrollable-container>
</template>

<script>
  import { cutUncheckedNodes } from '@/utils/cut-unchecked-nodes';
  import ScrollableContainer from '@/components/common/blocks/scrollable-container.vue';
  import Container from '@/components/common/blocks/container.vue';
  import Placeholder from '@/components/common/blocks/report-placeholder.vue';
  import { getTrafficByGroups, getTrafficDetailed } from '@/api/methods/reports/internet/index';
  import { getWebsiteStat } from '@/api/methods/reports/statistics/index';
  import Loader from '@/components/common/blocks/loader.vue';
  import { REPORT_ENUM } from '@/assets/scripts/reportEnum';
  import { getDataField } from '@/views/reports/users/worktime/table-field';
  import {
    ROUTE_TRAFFIC_BY_GROUPS,
    TRAFFIC_BY_GROUPS_COLUMNS,
    TRAFFIC_BY_PROCESSES_COLUMNS,
    TRAFFIC_BY_ADDRESSES_COLUMNS,
    SITE_TIME_COLUMNS,
    TRAFFIC_TABS,
    TRAFFIC_TABS_CHART,
  } from '@/views/reports/const/const';
  import Tabset from '@/components/common/tabs/single-view-tabs.vue';
  import VTable from '@/components/common/table/table.vue';
  import Search from '@/components/pages/computers/log/screenshots/components-header/search.vue';
  import StatTable from '@/views/reports/statistics/stat-table.vue';
  import { formatTraffic } from '@/helpers/formatter';
  import { LogData } from '@/domains/log/data';
  import { getDeepCopy } from '@/utils/deep-copy';
  import BarChart from '@/components/common/bars/bar-chart.vue';
  import PieChart from '@/components/common/bars/pie-chart.vue';
  import { convertSecondsToDays } from '@/helpers/format-date';
  import InternetTable from './internet-table.vue';

  export default {
    components: {
      ScrollableContainer,
      Container,
      Placeholder,
      InternetTable,
      Loader,
      Tabset,
      VTable,
      Search,
      StatTable,
      BarChart,
      PieChart,
    },

    filters: {
      formatTraffic,
    },
    data() {
      return {
        reportTree: [],
        processes: [],
        addresses: [],
        statData: [],
        total: null,
        flatTrafficCache: [],
        text: 'Для построения отчета нажмите кнопку',
        columns: TRAFFIC_BY_GROUPS_COLUMNS,
        columnsStat: SITE_TIME_COLUMNS,
        tabs: TRAFFIC_TABS,
        tabsChart: TRAFFIC_TABS_CHART,
        tabStyle: {
          padding: '14px 20px 17px 20px',
          fontSize: '16px',
        },
      };
    },
    computed: {
      selectedComputer() {
        return this.$store.getters['filters/selectedComputer'];
      },
      columnsDetailed() {
        return this.tabs[1].active ? TRAFFIC_BY_ADDRESSES_COLUMNS : TRAFFIC_BY_PROCESSES_COLUMNS;
      },
      pieChartData() {
        return {
          title: 'Время на сайтах',
          labels: this.flatTrafficCache?.timeSite.map((i) => i.nameUser),
          data: this.flatTrafficCache?.timeSite.map((i) => i.duration),
        };
      },
      pieChartUserData() {
        const field = this.tabs[0].active ? 'processName' : 'address';
        const labels = this.tableData.map((i) => i[field]);
        const sumTrafficIn = {
          title: 'Входящих',
          labels,
          data: this.tableData.map((i) => i.sumTrafficIn),
        };
        const sumTrafficOut = {
          title: 'Исходящих',
          labels,
          data: this.tableData.map((i) => i.sumTrafficOut),
        };

        return { sumTrafficIn, sumTrafficOut };
      },
      labels() {
        return this.flatTrafficCache?.traffic.map((i) => i.nameUser);
      },
      datasets() {
        return [
          {
            label: 'Входящий трафик',
            data: this.flatTrafficCache?.traffic.map((i) => i.sumTrafficIn),
            backgroundColor: '#4caf50',
          },
          {
            label: 'Исходящий трафик',
            data: this.flatTrafficCache?.traffic.map((i) => i.sumTrafficOut),
            backgroundColor: '#f44336',
          },
        ];
      },
      tableData() {
        const { filter } = this.$route.query;
        if (this.tabs[2].active) {
          if (!filter) return this.statData;
          return LogData.filterStatData(this.statData, filter);
        }
        const data = this.tabs[0].active ? this.processes : this.addresses;

        if (!filter) return data;
        return LogData.filterTable(data, ['processName', 'address'], filter);
      },
      isTrafficByGroups() {
        return ROUTE_TRAFFIC_BY_GROUPS === this.$route.name;
      },
      trafficOption() {
        return this.$store.getters['filters/trafficSelect'];
      },
      isTableReady() {
        return this.$store.getters['report/getStateTable'] === REPORT_ENUM.TABLE_READY;
      },
      isTableAwaiting() {
        return this.$store.getters['report/getStateTable'] === REPORT_ENUM.REQUEST_AWAITING;
      },
      isTableLoading() {
        return this.$store.getters['report/getStateTable'] === REPORT_ENUM.TABLE_LOADING;
      },
      dateRange() {
        return this.$store.getters['filters/formattedDateRange'];
      },
      checkedComputers() {
        return this.$store.getters['pageSpecificData/checkedComputersList'];
      },
      computersTree() {
        const tree = this.$store.getters['pageSpecificData/computersTree'];
        const treeCopy = getDeepCopy(tree);
        return cutUncheckedNodes(treeCopy);
      },
    },
    watch: {
      reportTree: {
        handler(newVal) {
          this.flatTrafficCache = this.buildFlatTraffic(newVal);
        },
        deep: true,
        immediate: true,
      },
      dateRange(oldValue, newValue) {
        if (
          oldValue.dateFrom.toString() !== newValue.dateFrom.toString() ||
          oldValue.dateTo.toString() !== newValue.dateTo.toString()
        ) {
          this.setStateAwaiting();
        }
      },
      trafficOption() {
        this.buildReport();
      },
      checkedComputers(oldArray, newArray) {
        if (oldArray !== newArray) this.setStateAwaiting();
      },
      isTrafficByGroups(oldVal, newVal) {
        if (oldVal !== newVal) this.setStateAwaiting();
      },
      selectedComputer(oldVal, newVal) {
        if (oldVal !== newVal) this.setStateAwaiting();
      },
    },
    mounted() {
      this.setStateAwaiting();
    },
    methods: {
      buildFlatTraffic(nodes) {
        const traffic = [];
        const timeSite = [];

        const walk = (arr) => {
          if (!arr) return;

          arr.forEach((node) => {
            if (node.children?.length) {
              walk(node.children);
            }

            if (node.type === 'folder') {
              return;
            }

            const t = node.trafficByGroups;

            if (!t) {
              return;
            }

            const inTraffic = t.sumTrafficIn ?? 0;
            const outTraffic = t.sumTrafficOut ?? 0;
            const duration = t.duration ?? 0;

            if (inTraffic || outTraffic) {
              traffic.push({
                nameUser: t.nameUser || node.name,
                sumTrafficIn: inTraffic,
                sumTrafficOut: outTraffic,
              });
            }
            if (duration) {
              timeSite.push({
                nameUser: t.nameUser || node.name,
                duration,
              });
            }
          });
        };

        walk(nodes);

        return { traffic, timeSite };
      },
      convertSecondsToDays(value) {
        return convertSecondsToDays(value);
      },
      formatTraffic(value) {
        return formatTraffic(value);
      },
      openNewTab(index) {
        const tabs = this.isTrafficByGroups ? this.tabsChart : this.tabs;

        tabs.forEach((t) => {
          t.active = false;
        });
        tabs[index].active = true;
      },
      getDataField(data, field) {
        return getDataField(data, field);
      },
      setStateAwaiting() {
        this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
      },
      async buildReport() {
        const isSelectedData = this.isTrafficByGroups ? this.checkedComputers.length : this.selectedComputer;
        if (this.dateRange.dateFrom && this.dateRange.dateTo && isSelectedData) {
          this.$store.commit('report/setStateTable', REPORT_ENUM.TABLE_LOADING);
          await this.fetch();
        }
      },
      async getTrafficByGroups() {
        const { data } = await getTrafficByGroups(
          this.checkedComputers,
          this.computersTree,
          this.dateRange.dateFrom,
          this.dateRange.dateTo,
          this.trafficOption,
        );

        if (data) {
          this.reportTree = data.result.computersTree;
          this.total = data.result.total;
          return data.result;
        }
        return null;
      },

      async getTrafficDetailed() {
        const { data } = await getTrafficDetailed(
          this.selectedComputer.id,
          this.dateRange.dateFrom,
          this.dateRange.dateTo,
          this.trafficOption,
        );
        const websiteStat = await getWebsiteStat(
          [this.selectedComputer],
          this.dateRange.dateFrom,
          this.dateRange.dateTo,
        );
        if (websiteStat) this.statData = websiteStat.data.result.users;
        if (data) {
          this.processes = data.result.processes;
          this.addresses = data.result.addresses;
          this.total = data.result.total;
          return data.result;
        }
        return null;
      },

      async fetch() {
        this.reportTree = [];
        let data = null;
        let name = '';
        if (this.isTrafficByGroups) {
          data = await this.getTrafficByGroups();
          name = 'Траффик по группам';
        } else {
          data = await this.getTrafficDetailed();
          name = 'Детальный траффик по ПК';
        }
        if (data) this.$store.commit('report/setStateTable', REPORT_ENUM.TABLE_READY);
        else this.setStateAwaiting();

        this.$store.commit('exportData/setTraffic', { name, data });
      },
    },
  };
</script>

<style lang="scss" module>
  ::-webkit-scrollbar {
    width: 4px;
  }

  ::-webkit-scrollbar-track {
    background: white;
  }

  ::-webkit-scrollbar-thumb {
    background: $branchColor;
    border-radius: 2px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: darkgray;
  }

  .scrollableContent {
    margin: 0 8px 0 0;
    padding: 0 12px 0 20px;
    box-shadow: $shadow;
    flex: 1;
    break-inside: auto;
    page-break-inside: auto;
  }

  .message {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .total {
    display: flex;
    gap: 5px;
    flex-direction: column;
    font-size: 14px;
    padding: 7px;
  }

  .bar {
    height: 500px;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .apexchart {
    display: flex;
    gap: 24px;
    flex-wrap: wrap;
    margin-top: 10px;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .chart {
    flex: 1 1 500px;
    min-width: 400px;
    height: 400px;
    break-inside: avoid;
    page-break-inside: avoid;
  }
</style>
